import type { DriverDocumentContentType, DriverStatusChangeInput, UpdateDriverInput } from '@chukua/common';
import { useState } from 'react';
import { ApiError } from '../../lib/api';
import { useToast } from '../../lib/use-toast';
import { attachDriverDocument, changeDriverStatus, createDriverDocumentUploadUrl, updateDriver, uploadToPresignedUrl } from './drivers.api';
import { useDriverRecord } from './use-driver-record';

export interface UploadDriverDocumentInput {
  documentTypeCode: string;
  file: File;
  contentType: DriverDocumentContentType;
  issuedAt?: string;
  expiresAt?: string;
}

/** A driver's own page: its record (`useDriverRecord`) plus every mutation the Details and Documents tabs need. */
export function useDriverDetail(id: string) {
  const { state, reload } = useDriverRecord(id);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  async function update(input: UpdateDriverInput): Promise<boolean> {
    setUpdateError(null);
    setSaving(true);
    try {
      await updateDriver(id, input);
      await reload();
      toast.success('Driver updated');
      return true;
    } catch (err) {
      setUpdateError(
        err instanceof ApiError && err.status === 409 ? 'A driver with this phone number already exists.' : 'Could not save — check the fields and try again.',
      );
      return false;
    } finally {
      setSaving(false);
    }
  }

  /** A row action with no form of its own: reload and toast on success, toast.error on failure (CLAUDE.md → Web conventions). */
  async function rowAction(action: () => Promise<void>, successMessage: string, errorMessage: string): Promise<boolean> {
    try {
      await action();
      await reload();
      toast.success(successMessage);
      return true;
    } catch {
      toast.error(errorMessage);
      return false;
    }
  }

  const changeStatus = (input: DriverStatusChangeInput) =>
    rowAction(() => changeDriverStatus(id, input), 'Driver status updated', "Couldn't update the driver's status");

  /** The three-call sequence: presign, PUT the file straight to object storage, then confirm — never through the API body. */
  async function uploadDocument(input: UploadDriverDocumentInput): Promise<boolean> {
    return rowAction(
      async () => {
        const upload = await createDriverDocumentUploadUrl(id, { documentTypeCode: input.documentTypeCode, contentType: input.contentType });
        await uploadToPresignedUrl(upload, input.file);
        await attachDriverDocument(id, {
          documentTypeCode: input.documentTypeCode,
          key: upload.key,
          contentType: input.contentType,
          issuedAt: input.issuedAt,
          expiresAt: input.expiresAt,
        });
      },
      'Document uploaded',
      "Couldn't upload the document",
    );
  }

  return { state, updateError, saving, update, changeStatus, uploadDocument };
}
